import { useEffect, useState } from "react";
import { Pencil, X, MapPin, Phone, Mail } from "lucide-react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@/components/common/GoogleIconButton";
import Skeleton from "@mui/material/Skeleton";
import Divider from "@mui/material/Divider";
import { viewProfile } from "@/api/auth";
import { useResolvedUrl } from "@/hooks/useResolvedUrl";
import { SessionsList } from "./SessionsList";
import { useAuthStore } from "@/stores/auth";

interface UserProfile {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  profileImageUrl?: string;
  email?: string;
  mobile?: string;
  dialCode?: string;
  address?: {
    addressType?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    country?: string;
    countryCode?: string;
    pinCode?: string;
  };
}

function formatAddress(address: UserProfile["address"]): string {
  if (!address) return "";
  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.pinCode,
    [address.country, address.countryCode].filter(Boolean).join(" "),
  ].filter((part): part is string => Boolean(part?.trim()));
  return parts.join(", ");
}

interface ViewProfileProps {
  onEdit: () => void;
  onClose: () => void;
}

export function ViewProfile({ onEdit, onClose }: ViewProfileProps) {
  const storeUser = useAuthStore((s) => s.user);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const avatarUrl = useResolvedUrl(profile?.profileImageUrl);

  useEffect(() => {
    let cancelled = false;
    const currentUser = useAuthStore.getState().user;
    viewProfile()
      .then((data) => {
        if (cancelled) return;
        const raw = data as Record<string, unknown>;
        const nextProfile: UserProfile = {
          firstName:
            (raw.firstname as string) ?? (raw.firstName as string) ?? "",
          lastName: (raw.lastname as string) ?? (raw.lastName as string) ?? "",
          displayName:
            (raw.displayName as string) ?? currentUser?.displayName ?? "",
          profileImageUrl: (raw.profileImageUrl as string) ?? "",
          email: (raw.email as string) ?? currentUser?.email ?? "",
          mobile: (raw.mobile as string) ?? "",
          dialCode: (raw.dialCode as string) ?? "",
          address: (raw.address as UserProfile["address"]) ?? undefined,
        };
        setProfile(nextProfile);
        const displayName = nextProfile.displayName || "";
        const email = nextProfile.email || "";
        if (displayName && email) {
          useAuthStore.setState((state) => {
            const user = state.user;
            if (
              !user ||
              (user.displayName === displayName && user.email === email)
            ) {
              return state;
            }
            return { user: { ...user, displayName, email } };
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProfile({
            displayName: currentUser?.displayName ?? "",
            email: currentUser?.email ?? "",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const initials = (profile?.displayName || storeUser?.displayName || "??")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
        <h2 className="text-base font-semibold tracking-tight">My Account</h2>
        <div className="flex items-center gap-1">
          <Button
            variant="text"
            size="small"
            sx={{ gap: "0.375rem" }}
            onClick={onEdit}
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
          <IconButton
            size="small"
            sx={{ width: 32, height: 32, color: "var(--muted-foreground)" }}
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </IconButton>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-2">
              <Skeleton variant="circular" width={64} height={64} />
              <Skeleton variant="text" width={160} />
              <Skeleton variant="text" width={208} />
            </div>
            <Skeleton variant="rounded" height={80} />
            <Skeleton variant="rounded" height={96} />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-col items-center gap-2 text-center">
              <Avatar
                src={avatarUrl || undefined}
                sx={{
                  width: 64,
                  height: 64,
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  color: "var(--primary)",
                  backgroundColor: "var(--primary-8)",
                  outline: "2px solid var(--border-40)",
                  outlineOffset: "2px",
                }}
              >
                {initials}
              </Avatar>
              <div>
                <p className="text-base font-semibold">
                  {profile?.displayName || "—"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {profile?.email}
                </p>
                {(profile?.dialCode || profile?.mobile) && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {profile.dialCode} {profile.mobile}
                  </p>
                )}
              </div>
            </div>

            {(profile?.address?.addressLine1 || profile?.email) && (
              <>
                <Divider />
                <div className="space-y-2.5 text-sm">
                  {profile?.email && (
                    <div className="flex items-center gap-2.5 text-muted-foreground">
                      <Mail className="h-4 w-4 shrink-0" />
                      <span className="truncate">{profile.email}</span>
                    </div>
                  )}
                  {(profile?.dialCode || profile?.mobile) && (
                    <div className="flex items-center gap-2.5 text-muted-foreground">
                      <Phone className="h-4 w-4 shrink-0" />
                      <span>
                        {profile.dialCode} {profile.mobile}
                      </span>
                    </div>
                  )}
                  {formatAddress(profile?.address) && (
                    <div className="flex items-start gap-2.5 text-muted-foreground">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{formatAddress(profile?.address)}</span>
                    </div>
                  )}
                </div>
              </>
            )}

            <Divider />
            <SessionsList />
          </div>
        )}
      </div>
    </div>
  );
}
